import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Learning } from '@shared/entities/learning';
import { ApiService } from '@shared/services';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { LearningService } from './learning.service';

const LOG_PREFIX = '[Api][LearningForm] ';

@Injectable({
  providedIn: 'root'
})
export class LearningFormService extends ApiService<Learning> {
  constructor(private http: HttpClient, private _learningService: LearningService) {
    const masterUrl = '/learning';
    const detailUrl = '/learning';
    super(Learning, masterUrl, detailUrl, undefined, 'learning-form');
  }

  findLearning(id: string): Observable<Learning | null> {
    return this.findOne(id).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'findLearning error', error);
        return of(null);
      })
    );
  }

  saveLearning(learningData: Learning): Observable<Learning | null> {
    this.logger.debug(LOG_PREFIX + 'saveLearning', learningData);
    return this.save(learningData).pipe(
      map((response) => {
        this.logger.debug(LOG_PREFIX + 'saveLearning success', response);
        this._learningService.refreshFilters();
        return response || learningData;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'saveLearning error', error);
        return of(error);
      })
    );
  }

  editLearning(learningData: Learning, learningId: string): Observable<Learning | null> {
    this.logger.debug(LOG_PREFIX + `editLearning with id: ${learningId}`, learningData);
    return this.save(learningData, learningId).pipe(
      map((response) => {
        this.logger.debug(LOG_PREFIX + 'editLearning success', response);
        this._learningService.refreshFilters();
        return response || learningData;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'editLearning error', error);
        return of(error);
      })
    );
  }

  findByFilter(id: string): Observable<Learning | null> {
    this.logger.debug(LOG_PREFIX + `findByFilter with id: ${id}`);

    this.setUrlParameter('id', id);

    return this.paginate(Learning, 1, 1).pipe(
      map((paginate) => {
        if (paginate && paginate.data && paginate.data.length > 0) {
          return paginate.data[0];
        }
        return null;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'findByFilter error', error);
        return of(null);
      })
    );
  }

  deleteLearning(id: string): Observable<boolean | null> {
    this.logger.debug(LOG_PREFIX + `deleteLearning with id: ${id}`);
    return this.remove(id).pipe(
      map(() => {
        this.logger.debug(LOG_PREFIX + 'deleteLearning success');
        this._learningService.refreshFilters();
        return true;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'deleteLearning error', error);
        return of(error);
      })
    );
  }

  saveLearningWithAttachments(learningData: Learning, files: Record<string, File>): Observable<Learning> {
    this.logger.debug(LOG_PREFIX + 'saveLearningWithAttachments', learningData);

    const formData = new FormData();
    formData.append('request', JSON.stringify(learningData));
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });

    return this.postMultipartData<Learning>('/learning', formData, Learning).pipe(
      tap((learning) => {
        this.logger.debug(LOG_PREFIX + 'saveLearningWithAttachments success', learning);
        this._learningService.refreshFilters();
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'saveLearningWithAttachments error', error);
        throw error;
      })
    );
  }

  updateLearningWithAttachments(
    learningId: string,
    learningData: Learning,
    files: Record<string, File>
  ): Observable<Learning> {
    this.logger.debug(LOG_PREFIX + `updateLearningWithAttachments with id: ${learningId}`, {
      learningData,
      fileCount: Object.keys(files).length
    });

    const formData = new FormData();
    learningData.id = learningId;
    formData.append('request', JSON.stringify(learningData));
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });

    return this.putMultipartData<Learning>(`/learning?id=${learningId}`, formData, Learning).pipe(
      tap((banner) => {
        this.logger.debug(LOG_PREFIX + 'updateLearningWithAttachments success', banner);
        this._learningService.refreshFilters();
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'updateLearningWithAttachments error', error);
        throw error;
      })
    );
  }
}
